import { formatBytes, t } from '@glory/i18n';
import { Camera, Trash2, Upload, ZoomIn, ZoomOut } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';

import { Avatar } from '../components/avatar.tsx';
import { Button } from '../components/button.tsx';
import { Dialog, DialogContent } from '../components/dialog.tsx';
import { Spinner } from '../components/spinner.tsx';
import { cn } from '../lib/cn.ts';
import { type CropArea, cropAvatar } from './avatar-crop.ts';
import { Field, type FieldPart, splitField, useFieldControl, useFieldState } from './field.tsx';
import type { FileUploader, UploadedFile } from './file-field.tsx';

/** Исходное фото больше не нужно: после кадрирования остаётся 512×512. */
const DEFAULT_MAX_SIZE = 20 * 1024 * 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export interface AvatarInputProps {
  readonly value: UploadedFile | null;
  readonly onValueChange: (value: UploadedFile | null) => void;
  readonly upload: FileUploader;
  /** Имя владельца: инициалы, пока фото нет. */
  readonly name: string;
  readonly maxSize?: number;
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
}

/**
 * Аватар: фото выбирается кнопкой или перетаскиванием, кадрируется по кругу (масштаб колёсиком,
 * ползунком или стрелками), загружается квадратом 512×512.
 */
export function AvatarInput({
  value,
  onValueChange,
  upload,
  name,
  maxSize = DEFAULT_MAX_SIZE,
  ...state
}: AvatarInputProps) {
  const field = useFieldState();
  const control = useFieldControl();
  const disabled = state.disabled ?? field?.disabled ?? false;
  const readOnly = state.readOnly ?? field?.readOnly ?? false;
  const locked = disabled || readOnly;

  const [source, setSource] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const area = useRef<CropArea | null>(null);
  const [uploading, setUploading] = useState<{ url: string; progress: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Локальные ссылки на картинки освобождаются, когда больше не показываются.
  useEffect(
    () => () => {
      if (source) URL.revokeObjectURL(source);
    },
    [source],
  );
  useEffect(
    () => () => {
      if (uploading) URL.revokeObjectURL(uploading.url);
    },
    [uploading],
  );

  const choose = (file: File) => {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError(t('fields.avatarNotImage'));
      return;
    }
    if (file.size > maxSize) {
      setError(t('errors.files.too_large', { limit: formatBytes(maxSize) }));
      return;
    }
    area.current = null;
    setCrop({ x: 0, y: 0 });
    setZoom(MIN_ZOOM);
    setSource(URL.createObjectURL(file));
  };

  const dropzone = useDropzone({
    accept: { 'image/*': [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: locked || uploading !== null,
    onDropAccepted: ([file]) => {
      if (file) choose(file);
    },
    onDropRejected: () => {
      setError(t('fields.avatarNotImage'));
    },
  });

  const save = async () => {
    if (!source) return;
    try {
      const file = await cropAvatar(source, area.current);
      setSource(null);
      const preview = URL.createObjectURL(file);
      setUploading({ url: preview, progress: 0 });
      const controller = new AbortController();
      const uploaded = await upload(file, {
        signal: controller.signal,
        onProgress: (progress) => {
          setUploading({ url: preview, progress });
        },
      });
      onValueChange(uploaded);
    } catch (e) {
      setSource(null);
      setError(
        e instanceof Error && e.message !== 'canvas' && e.message !== 'image'
          ? e.message
          : t('fields.avatarFailed'),
      );
    } finally {
      setUploading(null);
    }
  };

  const shown = uploading?.url ?? value?.previewUrl ?? null;

  return (
    <div className="flex items-center gap-4">
      <div
        {...dropzone.getRootProps()}
        className={cn(
          'relative shrink-0 rounded-full',
          dropzone.isDragActive && 'ring-2 ring-accent ring-offset-2 ring-offset-surface',
        )}
      >
        <input {...dropzone.getInputProps({ id: control.id })} aria-label={t('fields.avatarChoose')} />
        <Avatar name={name} src={shown} size="lg" className="size-16! text-title!" />
        {uploading ? (
          <span
            className="absolute inset-0 grid place-items-center rounded-full bg-overlay text-on-strong"
            role="progressbar"
            aria-label={t('fields.avatarUploading')}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(uploading.progress * 100)}
          >
            <Spinner />
          </span>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        {locked ? null : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" disabled={uploading !== null} onClick={dropzone.open}>
              {value ? <Camera /> : <Upload />} {value ? t('fields.avatarReplace') : t('fields.avatarUpload')}
            </Button>
            {value ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={uploading !== null}
                onClick={() => {
                  onValueChange(null);
                }}
              >
                <Trash2 /> {t('fields.avatarRemove')}
              </Button>
            ) : null}
          </div>
        )}
        {error ? (
          <p className="text-caption font-semibold text-danger" role="alert">
            {error}
          </p>
        ) : locked ? null : (
          <p className="text-caption text-muted">{t('fields.avatarHint', { limit: formatBytes(maxSize) })}</p>
        )}
      </div>

      <Dialog
        open={source !== null}
        onOpenChange={(open) => {
          if (!open) setSource(null);
        }}
      >
        {source ? (
          <DialogContent
            title={t('fields.avatarCropTitle')}
            description={t('fields.avatarCropLead')}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSource(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" onClick={() => void save()}>
                  {t('common.save')}
                </Button>
              </>
            }
          >
            <div className="relative h-72 overflow-hidden rounded-md bg-ink">
              <Cropper
                image={source}
                crop={crop}
                zoom={zoom}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_percent, pixels) => {
                  area.current = pixels;
                }}
              />
            </div>
            <label className="mt-3 flex items-center gap-2 text-muted [&_svg]:size-4">
              <ZoomOut aria-hidden="true" />
              <span className="sr-only">{t('fields.avatarZoom')}</span>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                onChange={(e) => {
                  setZoom(Number(e.target.value));
                }}
                className="h-1 flex-1 cursor-pointer accent-[var(--g-accent)]"
              />
              <ZoomIn aria-hidden="true" />
            </label>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}

export interface AvatarFieldProps extends FieldPart, Omit<AvatarInputProps, keyof FieldPart> {}

export function AvatarField(props: AvatarFieldProps) {
  const [field, input] = splitField(props);
  return (
    <Field {...field}>
      <AvatarInput {...input} />
    </Field>
  );
}
